import { createLatestIdentityLoader } from '@/components/brands/identity-editor-state';
import { apiRequest } from '@/lib/queryClient';
import { IdentityEditor } from '@/components/brands/IdentityEditor';
import type { IdentitySettings } from '@shared/identity';
// Approved product identity with reviewable photo scan suggestions.

import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

interface ProductIdentityTabProps {
  brandId: number;
  /** When provided, renders an "Add product" button that calls this. */
  onAddProduct?: () => void;
  /** When provided, each product card renders an Edit button. */
  onEditProduct?: (product: ProductRow) => void;
  /** When provided, each product card renders a Delete button. */
  onDeleteProduct?: (product: ProductRow) => void;
  /** Bump to force a reload (e.g. after a product was added in the parent). */
  refreshSignal?: number;
}

export type ProductRow = {
  id: number;
  identitySettings?: IdentitySettings | null;
  name: string;
  description: string | null;
  productUrl: string | null;
  colors: string[] | null;
  packagingDescription: string | null;
  visualAnalyzedAt: string | null;
  photos: string[] | null;
};

export function ProductIdentityTab({ brandId, onAddProduct, onEditProduct, onDeleteProduct, refreshSignal }: ProductIdentityTabProps) {
  const { toast } = useToast();
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [polls, setPolls] = useState(0);

  const [loadError, setLoadError] = useState('');
  const loader = useRef(createLatestIdentityLoader());
  const currentBrand = useRef(brandId);
  const loadedBrandRef = useRef<number | null>(null);
  if (currentBrand.current !== brandId) {
    currentBrand.current = brandId;
    loader.current.invalidate();
  }
  const load = async (opts?: { silent?: boolean }) => {
    if (currentBrand.current !== brandId) return;
    if (!opts?.silent) setLoading(true);
    await loader.current.load(async () => {
      const res = await apiRequest('GET', `/api/brands/${brandId}/products`);
      const rows = await res.json();
      if (!Array.isArray(rows)) throw new Error('Unexpected product response');
      return rows as ProductRow[];
    }, rows => { setProducts(rows); setLoadError(''); }, error => {
      setLoadError(error.message);
    }, () => setLoading(false));
  };
  useEffect(() => {
    setPolls(0);
    const brandChanged = loadedBrandRef.current !== brandId;
    loadedBrandRef.current = brandId;
    if (brandChanged) { setProducts([]); setLoadError(''); setAnalyzingId(null); }
    void load({ silent: !brandChanged });
    return () => { loader.current.invalidate(); };
  }, [brandId, refreshSignal]);

  // A freshly-added product analyzes in the background (~a few seconds). The
  // tab can load before that finishes and would otherwise sit on "not analyzed
  // yet" forever. Poll while any product has a photo but no colors yet, up to a
  // cap (so a genuinely-undetectable photo doesn't poll endlessly).
  useEffect(() => {
    const pending = products.some(
      (p) => (p.photos?.length ?? 0) > 0 && (!p.colors || p.colors.length === 0) && !p.identitySettings?.suggestions?.length,
    );
    if (!pending || polls >= 10) return;
    const t = setTimeout(() => {
      setPolls((n) => n + 1);
      load({ silent: true });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, polls]);

  const reanalyze = async (id: number) => {
    setAnalyzingId(id);
    try {
      const res = await apiRequest("POST", `/api/products/${id}/analyze-visual`);
      if (!res.ok) throw new Error("failed");
      if (currentBrand.current !== brandId) return;
      toast({ title: "Product suggestions ready", description: "Review the proposed colors and packaging below. Approved values are unchanged." });
      // Silent: the row's own spinner (analyzingId) already reports progress —
      // blanking the whole tab to refresh one row is the v7.03 flicker.
      await load({ silent: true });
    } catch {
      if (currentBrand.current !== brandId) return;
      toast({ title: "Re-analyze failed", variant: "destructive" });
    } finally {
      if (currentBrand.current === brandId) setAnalyzingId(null);
    }
  };

  if (loading || loadedBrandRef.current !== brandId) return <div className="p-6 text-sm text-slate-400">Loading products…</div>;

  return (
    <div className="p-5 space-y-4" data-testid="product-identity-tab">
      {loadError && <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Unable to refresh products. Existing edits are preserved. <button className="underline" onClick={() => load({silent:true})}>Retry</button><p className="text-xs mt-1">{loadError}</p></div>}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Product Identity
            {polls > 0 && polls < 10 && products.some(p => (p.photos?.length ?? 0) > 0 && !p.colors?.length && !p.identitySettings?.suggestions?.length) && <span className="ml-2 text-[10px] font-normal text-slate-400">scanning new product…</span>}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Edit each product’s approved palette and packaging. Photo scans create suggestions for review before replacing approved values.
          </p>
        </div>
        {onAddProduct && (
          <button
            onClick={onAddProduct}
            className="text-xs px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 flex-shrink-0"
          >
            + Add product
          </button>
        )}
      </div>

      {products.length === 0 ? (
        onAddProduct ? (
          <button
            onClick={onAddProduct}
            className="w-full rounded-lg border-2 border-dashed border-slate-200 py-8 text-center hover:border-slate-300"
            data-testid="button-products-empty-add"
          >
            <span className="block text-sm font-medium text-slate-500">No products yet</span>
            <span className="block text-xs text-slate-400 mt-1">+ Add a product — it scans automatically</span>
          </button>
        ) : (
          <p className="text-xs text-slate-400">No products yet.</p>
        )
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {products.map((p) => {
            const processing = analyzingId === p.id;
            return (
            <div key={p.id} className={`rounded-lg border bg-white p-3 ${processing ? 'border-blue-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {p.photos?.[0] && (
                    <img src={p.photos[0]} alt="" className="w-10 h-10 rounded object-cover border border-slate-200 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-[10px]">
                      {processing ? (
                        <span className="inline-flex items-center gap-1 text-blue-500 font-medium">
                          <span className="inline-block w-2.5 h-2.5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                          Processing…
                        </span>
                      ) : (
                        <span className="text-slate-400">{p.visualAnalyzedAt ? `analyzed ${new Date(p.visualAnalyzedAt).toLocaleDateString()}` : 'not analyzed yet'}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => reanalyze(p.id)}
                    disabled={processing}
                    className="text-xs px-3 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                  >
                    {processing ? 'Scanning…' : 'Scan photo'}
                  </button>
                  {onEditProduct && (
                    <button
                      onClick={() => onEditProduct(p)}
                      className="text-xs px-3 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                      data-testid={`button-product-tab-edit-${p.id}`}
                    >
                      Edit
                    </button>
                  )}
                  {onDeleteProduct && (
                    <button
                      onClick={() => onDeleteProduct(p)}
                      className="text-xs px-3 py-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100"
                      data-testid={`button-product-tab-delete-${p.id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              <IdentityEditor record={p} kind="products" onUpdated={updated => { loader.current.invalidate(); setProducts(items => items.map(item => item.id === updated.id ? updated : item)); }}/>

            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
